import React from 'react';
import { Nav, Container, Row, Col } from 'react-bootstrap';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/free-solid-svg-icons';
import Flag from 'react-world-flags';
import LanguageSwitcher from './LanguageSwitcher';
import { faFacebook, faTwitter, faYoutube, faInstagram, faPinterest } from '@fortawesome/free-brands-svg-icons';

// Custom Link component that scrolls to top on click
const ScrollToTopLink = ({ to, children, className }) => {
    const handleClick = () => {
        window.scrollTo(0, 0);
    };

    return (
        <Link to={to} onClick={handleClick} className={className}>
            {children}
        </Link>
    );
};

const Footer = () => {
    const { lang } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Fallback to 'en' if lang is undefined
    const currentLang = lang || 'en';

    return (
        <footer className="seo-footer bg-light py-5 mt-5">
            <Container className="p-4">
                <Row>
                    <Col md={12} className="p-4">
                        <h5 className="text-center font-weight-bold">{t('global.h5_dwonload')}</h5>
                        <div className="d-flex justify-content-center">
                            <a href="https://play.google.com/store/apps/details?id=com.app.petshome" target="_blank" rel="noopener noreferrer" className="mr-3" title="Download Pets Home App from Google Play Store">
                                <img style={{ width: '180px', height: 'auto', marginRight: "10px" }} src="/google-play-badge.svg" alt="Download Pets Home App on Google Play" />
                            </a>
                            <a href="https://apps.apple.com/app/apple-store/id1549828591?pt=581834&ct=desktop&mt=8" target="_blank" rel="noopener noreferrer" title="Download Pets Home App from Apple App Store">
                                <img style={{ width: '180px', height: 'auto' }} src="/app-store-badge.svg" alt="Download Pets Home App on App Store" />
                            </a>
                        </div>
                        <div className="d-flex justify-content-center">
                            <div>
                                <FontAwesomeIcon icon={faStar} color="gold" />
                                <FontAwesomeIcon icon={faStar} color="gold" />
                                <FontAwesomeIcon icon={faStar} color="gold" />
                                <FontAwesomeIcon icon={faStar} color="gold" />
                                <FontAwesomeIcon icon={faStar} color="gold" />
                            </div>
                        </div>
                        <div className="d-flex justify-content-center mt-4">
                            <img style={{ width: '130px', height: 'auto', marginRight: '10px' }} src="/android-adobe-express-qr-code.png" alt="Android QR Code" className="mr-3" />
                            <img style={{ width: '130px', height: 'auto' }} src="/ios-express-qr-code.png" alt="iOS QR Code" />
                        </div>
                        <p className="text-center mt-3 d-none d-md-block">{t('Scan the QR codes below for quick access:')}</p>

                        {/* Social Sharing Buttons */}
                        <div className="social-sharing text-center my-4">
                            <p>{t('global.share_p')}</p>
                            <a href="https://www.facebook.com/sharer/sharer.php?u=https://www.petshome.app/" target="_blank" rel="noopener noreferrer">
                                <FontAwesomeIcon icon={faFacebook} size="2x" className="mx-2" />
                            </a>
                            <a href="https://twitter.com/intent/tweet?url=https://www.petshome.app/&text=Sell%20Your%20Pet%20Online%20for%20Free%20with%20Pets%20Home%20App" target="_blank" rel="noopener noreferrer">
                                <FontAwesomeIcon icon={faTwitter} size="2x" className="mx-2" />
                            </a>
                            <a href="https://www.instagram.com/petshomeapp/" target="_blank" rel="noopener noreferrer">
                                <FontAwesomeIcon icon={faInstagram} size="2x" className="mx-2" />
                            </a>
                            <a href="https://www.pinterest.com/petshomeapp/" target="_blank" rel="noopener noreferrer">
                                <FontAwesomeIcon icon={faPinterest} size="2x" className="mx-2" />
                            </a>
                            <a href="https://www.youtube.com/channel/UCTjh3nmz0cVGaX0MnHl6elA" target="_blank" rel="noopener noreferrer">
                                <FontAwesomeIcon icon={faYoutube} size="2x" className="mx-2" />
                            </a>
                        </div>
                    </Col>
                </Row>
            </Container>
            <div className="container">
                <Nav className="justify-content-center">
                    <Nav.Item>
                        <Nav.Link as={ScrollToTopLink} to={`/${currentLang}/terms-of-use`}>
                            Terms
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link as={ScrollToTopLink} to={`/${currentLang}/privacy-policy`}>
                            Privacy Policy
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link as={ScrollToTopLink} to={`/${currentLang}/partnership-opportunities`}>
                            {t('global.LetsPartner')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link as={ScrollToTopLink} to={`/${currentLang}/affiliate`}>
                            {t('global.Affiliate')}
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link as={ScrollToTopLink} to={`/${currentLang}/about-us`}>
                            {t('aboutUs.title')}
                        </Nav.Link>
                    </Nav.Item>
                    <LanguageSwitcher />
                </Nav>
            </div>

            <Container>
                <hr />

                <Row className="popular-locations mb-4">
                    <Col xs={12}>
                        <h5 className="mb-3">Find Pets by Location</h5>
                    </Col>

                    {/* Pet Classified Ads Main Link */}
                    <Col xs={12} className="text-center mb-3">
                        <h6>
                            <ScrollToTopLink to={`/${currentLang}/pet-classified-ads`} className="btn btn-outline-primary">
                                Browse All Pet Classified Ads By Location
                            </ScrollToTopLink>
                        </h6>
                    </Col>

                    {/* United States */}
                    <Col md={3} sm={6} className="mb-3">
                        <h6><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states`}>United States</ScrollToTopLink></h6>
                        <ul className="list-unstyled small">
                            <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states/california`}>California</ScrollToTopLink>
                                <ul className="list-unstyled ml-2 small">
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states/california/los-angeles`}>Los Angeles</ScrollToTopLink></li>
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states/california/san-francisco`}>San Francisco</ScrollToTopLink></li>
                                </ul>
                            </li>
                            <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states/texas`}>Texas</ScrollToTopLink>
                                <ul className="list-unstyled ml-2 small">
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states/texas/houston`}>Houston</ScrollToTopLink></li>
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states/texas/dallas`}>Dallas</ScrollToTopLink></li>
                                </ul>
                            </li>
                            <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states/georgia`}>Georgia</ScrollToTopLink>
                                <ul className="list-unstyled ml-2 small">
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states/georgia/atlanta`}>Atlanta</ScrollToTopLink></li>
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-states/georgia/duluth`}>Duluth</ScrollToTopLink></li>
                                </ul>
                            </li>
                        </ul>
                    </Col>

                    {/* United Kingdom */}
                    <Col md={3} sm={6} className="mb-3">
                        <h6><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-kingdom`}>United Kingdom</ScrollToTopLink></h6>
                        <ul className="list-unstyled small">
                            <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-kingdom/england`}>England</ScrollToTopLink>
                                <ul className="list-unstyled ml-2 small">
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-kingdom/england/london`}>London</ScrollToTopLink></li>
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-kingdom/england/manchester`}>Manchester</ScrollToTopLink></li>
                                </ul>
                            </li>
                            <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-kingdom/scotland`}>Scotland</ScrollToTopLink>
                                <ul className="list-unstyled ml-2 small">
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-kingdom/scotland/edinburgh`}>Edinburgh</ScrollToTopLink></li>
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/united-kingdom/scotland/glasgow`}>Glasgow</ScrollToTopLink></li>
                                </ul>
                            </li>
                        </ul>
                    </Col>

                    {/* Canada */}
                    <Col md={3} sm={6} className="mb-3">
                        <h6><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/canada`}>Canada</ScrollToTopLink></h6>
                        <ul className="list-unstyled small">
                            <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/canada/ontario`}>Ontario</ScrollToTopLink>
                                <ul className="list-unstyled ml-2 small">
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/canada/ontario/toronto`}>Toronto</ScrollToTopLink></li>
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/canada/ontario/ottawa`}>Ottawa</ScrollToTopLink></li>
                                </ul>
                            </li>
                            <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/canada/british-columbia`}>British Columbia</ScrollToTopLink>
                                <ul className="list-unstyled ml-2 small">
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/canada/british-columbia/vancouver`}>Vancouver</ScrollToTopLink></li>
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/canada/british-columbia/victoria`}>Victoria</ScrollToTopLink></li>
                                </ul>
                            </li>
                        </ul>
                    </Col>

                    {/* Australia */}
                    <Col md={3} sm={6} className="mb-3">
                        <h6><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/australia`}>Australia</ScrollToTopLink></h6>
                        <ul className="list-unstyled small">
                            <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/australia/new-south-wales`}>New South Wales</ScrollToTopLink>
                                <ul className="list-unstyled ml-2 small">
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/australia/new-south-wales/sydney`}>Sydney</ScrollToTopLink></li>
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/australia/new-south-wales/newcastle`}>Newcastle</ScrollToTopLink></li>
                                </ul>
                            </li>
                            <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/australia/victoria`}>Victoria</ScrollToTopLink>
                                <ul className="list-unstyled ml-2 small">
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/australia/victoria/melbourne`}>Melbourne</ScrollToTopLink></li>
                                    <li><ScrollToTopLink to={`/${currentLang}/pet-classified-ads/australia/victoria/geelong`}>Geelong</ScrollToTopLink></li>
                                </ul>
                            </li>
                        </ul>
                    </Col>
                </Row>

                <hr />

                {/* Pet Types Section */}
                <Row className="pet-types-section mb-4">
                    <Col xs={12}>
                        <h5 className="mb-3">Popular Pet Types</h5>
                    </Col>

                    {/* Dogs - Column 1 */}
                    <Col md={3} sm={6} className="mb-3">
                        <h6>Dogs - United States</h6>
                        <ul className="list-unstyled small">
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Dogs for Adoption</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Dogs for Sale</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Yorkshire Terrier</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>French Bulldog</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Labrador Retriever</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Golden Retriever</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>German Shepherd</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Poodle</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Bulldog</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Beagle</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Teacup Puppies</ScrollToTopLink></li>
                        </ul>
                    </Col>

                    {/* Dogs - Column 2 */}
                    <Col md={3} sm={6} className="mb-3">
                        <h6>Dogs - UK & Germany</h6>
                        <ul className="list-unstyled small">
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Cockapoo</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Dachshund</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Border Collie</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Cocker Spaniel</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Staffordshire Bull Terrier</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Miniature Schnauzer</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Deutsche Dogge</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Deutscher Schäferhund</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Dackel</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Cavalier King Charles Spaniel</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Labradoodle</ScrollToTopLink></li>
                        </ul>
                    </Col>

                    {/* Cats */}
                    <Col md={3} sm={6} className="mb-3">
                        <h6>Cats</h6>
                        <ul className="list-unstyled small">
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Cats for Adoption</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Cats for Sale</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Persian</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Siamese</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Maine Coon</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>British Shorthair</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Ragdoll</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Bengal</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Sphynx</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Scottish Fold</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Exotic Shorthair</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Abyssinian</ScrollToTopLink></li>
                        </ul>
                    </Col>

                    {/* Other Pets */}
                    <Col md={3} sm={6} className="mb-3">
                        <h6>Other Popular Pets</h6>
                        <ul className="list-unstyled small">
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Rabbits</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Guinea Pigs</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Hamsters</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Ferrets</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Parakeets</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Parrots</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Cockatiel</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Bearded Dragons</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Turtles</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Geckos</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Fish</ScrollToTopLink></li>
                            <li><ScrollToTopLink to={`/${currentLang}/findpets`}>Hedgehogs</ScrollToTopLink></li>
                        </ul>
                    </Col>
                </Row>

                {/* Additional Row for Specific Pet Types by Country */}
                <Row className="pet-types-by-country mb-4">
                    <Col md={4} sm={12} className="mb-3">
                        <h6>Popular Pets in US</h6>
                        <div className="d-flex flex-wrap">
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">French Bulldog</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Goldendoodle</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Teacup Yorkie</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Pitbull</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Husky</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Bengal Cat</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Savannah Cat</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Ball Python</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Sugar Glider</ScrollToTopLink>
                        </div>
                    </Col>

                    <Col md={4} sm={12} className="mb-3">
                        <h6>Popular Pets in UK</h6>
                        <div className="d-flex flex-wrap">
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">French Bulldog</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Cockapoo</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Labradoodle</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Dachshund</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Shih Tzu</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">English Bulldog</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">British Shorthair</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Ragdoll</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Pug</ScrollToTopLink>
                        </div>
                    </Col>

                    <Col md={4} sm={12} className="mb-3">
                        <h6>Popular Pets in Germany</h6>
                        <div className="d-flex flex-wrap">
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Deutscher Schäferhund</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Dackel</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Labrador Retriever</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Französische Bulldogge</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Chihuahua</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Maine Coon</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Bengal</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Kaninchen</ScrollToTopLink>
                            <ScrollToTopLink to={`/${currentLang}/findpets`} className="badge bg-light text-dark m-1">Zwerghamster</ScrollToTopLink>
                        </div>
                    </Col>
                </Row>

                <hr />

                <Row className="text-center mt-4">
                    <Col xs={12}>
                        <p>
                            <small>
                                &copy; {new Date().getFullYear()} Pets Home App. All rights reserved.
                                <br />
                                Connecting pet lovers with their perfect companions worldwide.
                            </small>
                        </p>
                        <div className="app-download-links">
                            <a href="https://play.google.com/store/apps/details?id=com.app.petshome" target="_blank" rel="noopener noreferrer" className="me-2">
                                <img src="/google-play-badge.svg" alt="Download on Google Play" height="40" />
                            </a>
                            <a href="https://apps.apple.com/app/apple-store/id1549828591?pt=581834&ct=desktop&mt=8" target="_blank" rel="noopener noreferrer">
                                <img src="/app-store-badge.svg" alt="Download on App Store" height="40" />
                            </a>
                        </div>
                    </Col>
                </Row>
            </Container>
        </footer>
    );
};

export default Footer;